import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Map } from 'immutable';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { connect } from 'react-redux';

import Tooltip from '../../components/helpers/DataToolTip';
import BaseButton from '../../components/helpers/Button';
import { useDirectorDashboardPusher, DIRECTOR_DASHBOARD_UPDATED } from '../../hooks/useDirectorDashboardPusher';
import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { loadStatistics, shareLink } from '../../actions/directorDashboard';
import { ButtonsGroup, Button } from '../../components/ButtonsGroup';
import { DateRangePicker } from '../../components/DateRangePicker';
import { DATE_ISO_FORMAT, MONTH_DAY_SHORT_FORMAT } from '../../utils/dateFormat';
import { SummaryTile } from '../../components/DirectorDashboard/StatisticReport/SummaryTile';
import { StatisticReportTable } from '../../components/DirectorDashboard/StatisticReport/StatisticReportTable';
import config from '../../config';
import downloadFile from '../../utils/downloadFile';

import style from './style.module.css';

dayjs.extend(quarterOfYear);
dayjs.extend(weekOfYear);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const getCurrentWeek = () => {
  const dateFrom = dayjs()
    .startOf('week')
    .add(1, 'day');
  const dateTo = dayjs()
    .endOf('week')
    .add(1, 'day');
  const comparisonDateFrom = dayjs()
    .startOf('week')
    .subtract(6, 'day');
  const comparisonDateTo = dayjs()
    .endOf('week')
    .subtract(6, 'day');

  return { dateFrom, dateTo, comparisonDateFrom, comparisonDateTo };
};

const PRESET_RANGES = {
  currentWeek: getCurrentWeek,
  lastWeek: () => ({
    dateFrom: dayjs()
      .startOf('week')
      .subtract(6, 'day'),
    dateTo: dayjs()
      .endOf('week')
      .subtract(6, 'day'),
    comparisonDateFrom: dayjs()
      .startOf('week')
      .subtract(13, 'day'),
    comparisonDateTo: dayjs()
      .endOf('week')
      .subtract(13, 'day'),
  }),
  MTD: () => ({
    dateFrom: dayjs().startOf('month'),
    dateTo: dayjs(),
    comparisonDateFrom: dayjs()
      .subtract(1, 'month')
      .startOf('month'),
    comparisonDateTo: dayjs()
      .subtract(1, 'month')
      .endOf('month'),
  }),
  lastMonth: () => ({
    dateFrom: dayjs()
      .subtract(1, 'month')
      .startOf('month'),
    dateTo: dayjs()
      .subtract(1, 'month')
      .endOf('month'),
    comparisonDateFrom: dayjs()
      .subtract(2, 'month')
      .startOf('month'),
    comparisonDateTo: dayjs()
      .subtract(2, 'month')
      .endOf('month'),
  }),
  QTD: () => ({
    dateFrom: dayjs().startOf('quarter'),
    dateTo: dayjs(),
    comparisonDateFrom: dayjs()
      .subtract(1, 'quarter')
      .startOf('quarter'),
    comparisonDateTo: dayjs()
      .subtract(1, 'quarter')
      .endOf('quarter'),
  }),
  lastQuarter: () => ({
    dateFrom: dayjs()
      .subtract(1, 'quarter')
      .startOf('quarter'),
    dateTo: dayjs()
      .subtract(1, 'quarter')
      .endOf('quarter'),
    comparisonDateFrom: dayjs()
      .subtract(2, 'quarter')
      .startOf('quarter'),
    comparisonDateTo: dayjs()
      .subtract(2, 'quarter')
      .endOf('quarter'),
  }),
  YTD: () => ({
    dateFrom: dayjs().startOf('year'),
    dateTo: dayjs(),
    comparisonDateFrom: dayjs()
      .subtract(1, 'year')
      .startOf('year'),
    comparisonDateTo: dayjs()
      .subtract(1, 'year')
      .endOf('year'),
  }),
  lastYear: () => ({
    dateFrom: dayjs()
      .subtract(1, 'year')
      .startOf('year'),
    dateTo: dayjs()
      .subtract(1, 'year')
      .endOf('year'),
    comparisonDateFrom: dayjs()
      .subtract(2, 'year')
      .startOf('year'),
    comparisonDateTo: dayjs()
      .subtract(2, 'year')
      .endOf('year'),
  }),
};

const PRESET_LABELS = {
  currentWeek: ({ dateFrom, dateTo }) =>
    `Current Week (${dateFrom.format(MONTH_DAY_SHORT_FORMAT)} – ${dateTo.format(MONTH_DAY_SHORT_FORMAT)})`,
  lastWeek: ({ dateFrom, dateTo }) =>
    `Last Week (${dateFrom.format(MONTH_DAY_SHORT_FORMAT)} – ${dateTo.format(MONTH_DAY_SHORT_FORMAT)})`,
  MTD: ({ dateFrom, dateTo }) =>
    `Month to Date (${dateFrom.format(MONTH_DAY_SHORT_FORMAT)} – ${dateTo.format(MONTH_DAY_SHORT_FORMAT)})`,
  lastMonth: ({ dateFrom, dateTo }) =>
    `Last Month (${dateFrom.format(MONTH_DAY_SHORT_FORMAT)} – ${dateTo.format(MONTH_DAY_SHORT_FORMAT)})`,
  QTD: ({ dateFrom }) => `Quarter to Date (Q${dateFrom.quarter()})`,
  lastQuarter: ({ dateFrom }) => `Last Quarter (Q${dateFrom.quarter()})`,
  YTD: ({ dateFrom, dateTo }) =>
    `Year to Date (${dateFrom.format(MONTH_DAY_SHORT_FORMAT)} – ${dateTo.format(MONTH_DAY_SHORT_FORMAT)})`,
  lastYear: ({ dateFrom }) => `Last Year (${dateFrom.format('YYYY')})`,
};

const findMatchingRangeKey = period => {
  if (!period.dateFrom || !period.dateTo) return null;

  const matchedKey = Object.keys(PRESET_RANGES).find(key => {
    const range = PRESET_RANGES[key]();

    return period.dateFrom.isSame(range.dateFrom, 'day') && period.dateTo.isSame(range.dateTo, 'day');
  });

  return matchedKey ?? null;
};

const StatisticReportContainer = ({ statistics, statisticsLoading, loadStatistics, shareLink, user }) => {
  const { subscribe, unsubscribe } = useDirectorDashboardPusher(user);
  const [firmRollupText, setFirmRollupText] = useState('');
  const [period, setPeriod] = useState(getCurrentWeek());

  const activeRangeKey = useMemo(() => findMatchingRangeKey(period), [period]);

  const loadStatisticsWithPeriod = useCallback(
    ({ dateFrom, dateTo, comparisonDateFrom, comparisonDateTo }, showLoading = true) => {
      loadStatistics(
        {
          dateFrom: dateFrom.format(DATE_ISO_FORMAT),
          dateTo: dateTo.format(DATE_ISO_FORMAT),
          comparisonDateFrom: comparisonDateFrom.format(DATE_ISO_FORMAT),
          comparisonDateTo: comparisonDateTo.format(DATE_ISO_FORMAT),
        },
        showLoading,
      );
    },
    [loadStatistics],
  );

  const isPeriodComplete = Boolean(
    period.dateFrom && period.dateTo && period.comparisonDateFrom && period.comparisonDateTo,
  );

  useEffect(() => {
    subscribe(DIRECTOR_DASHBOARD_UPDATED, ({ data }) => {
      if (!isPeriodComplete) return;

      const eventDate = dayjs(data.date);

      if (eventDate.isSameOrAfter(period.dateFrom) && eventDate.isSameOrBefore(period.dateTo)) {
        loadStatisticsWithPeriod(period, false);
      }
    });

    return () => {
      unsubscribe(DIRECTOR_DASHBOARD_UPDATED);
    };
  }, [period, isPeriodComplete, loadStatisticsWithPeriod]);

  useEffect(() => {
    const range = getCurrentWeek();

    setFirmRollupText(PRESET_LABELS.currentWeek(range));
    setPeriod(range);
  }, []);

  useEffect(() => {
    if (isPeriodComplete) {
      loadStatisticsWithPeriod(period);
    }
  }, [period, isPeriodComplete, loadStatisticsWithPeriod]);

  const handleGetStatistics = useCallback(value => {
    const getRange = PRESET_RANGES[value];

    if (!getRange) return;

    const range = getRange();

    setFirmRollupText(PRESET_LABELS[value](range));
    setPeriod(range);
  }, []);

  const handleDateRangeChange = useCallback(({ dateFrom, dateTo }) => {
    if (!dateFrom || !dateTo) {
      setPeriod(previousPeriod => ({ ...previousPeriod, dateFrom, dateTo }));

      return;
    }

    const rangeLengthInDays = dateTo.diff(dateFrom, 'day') + 1;
    const comparisonDateTo = dateFrom.subtract(1, 'day');
    const comparisonDateFrom = comparisonDateTo.subtract(rangeLengthInDays - 1, 'day');

    setFirmRollupText(
      `Custom Range (${dateFrom.format(MONTH_DAY_SHORT_FORMAT)} – ${dateTo.format(MONTH_DAY_SHORT_FORMAT)})`,
    );
    setPeriod({ dateFrom, dateTo, comparisonDateFrom, comparisonDateTo });
  }, []);

  const handleExportStatistics = useCallback(() => {
    if (!isPeriodComplete) return;

    const hash = dayjs().unix();
    const url = `${config.API_BASE_URL}/api/v1/director_dashboard/statistics/export`;

    downloadFile({
      url,
      params: {
        date_from: period.dateFrom.format(DATE_ISO_FORMAT),
        date_to: period.dateTo.format(DATE_ISO_FORMAT),
        comparison_date_from: period.comparisonDateFrom.format(DATE_ISO_FORMAT),
        comparison_date_to: period.comparisonDateTo.format(DATE_ISO_FORMAT),
        hash,
      },
    });
  }, [period, isPeriodComplete]);

  const handleShareLink = useCallback(
    data =>
      new Promise(resolve => {
        if (!isPeriodComplete) {
          resolve(null);

          return;
        }

        shareLink(
          {
            ...data,
            dateFrom: period.dateFrom.format(DATE_ISO_FORMAT),
            dateTo: period.dateTo.format(DATE_ISO_FORMAT),
          },
          {
            afterSuccess: ({ response }) => {
              window.open(response.data.link, '_blank');
              resolve(response);
            },
            afterError: () => {
              resolve(null);
            },
          },
        );
      }),
    [period, isPeriodComplete],
  );

  const handleRefresh = useCallback(() => {
    loadStatisticsWithPeriod(period);
  }, [period]);

  return (
    <>
      <div className="row d-flex align-items-center pt10">
        <div className="col-md-5">
          <h3 className="text-primary-dark text-bold">Statistics Report</h3>
          <h6>
            Live KPI rollup. Click any Module or Team row to drill in. Numbers refresh against the period selected.
          </h6>
        </div>
        <div className="col-md-7 text-right">
          <div className={style.dateRangeToolbar}>
            <Tooltip content="Refresh">
              <BaseButton aria-label="Refresh statistics" onClick={handleRefresh}>
                <i aria-hidden="true" className="fa fa-refresh mr0" />
              </BaseButton>
            </Tooltip>
            <DateRangePicker dateFrom={period.dateFrom} dateTo={period.dateTo} onChange={handleDateRangeChange} />
            <ButtonsGroup onChange={handleGetStatistics} value={activeRangeKey}>
              <Button label="Current Week" value="currentWeek" />
              <Button label="Last Week" value="lastWeek" />
              <Button label="MTD" value="MTD" />
              <Button label="Last Month" value="lastMonth" />
              <Button label="QTD" value="QTD" />
              <Button label="Last Quarter" value="lastQuarter" />
              <Button label="YTD" value="YTD" />
              <Button label="Last Year" value="lastYear" />
            </ButtonsGroup>
            <BaseButton onClick={handleExportStatistics}>Export</BaseButton>
          </div>
        </div>
      </div>
      <div className="row mt10 mb20">
        <div className="col-md-12">
          <div className={style.panelStatistic}>
            <SummaryTile
              changePercent={statistics.getIn(['summary', 'approachesE', 'changePercent'], '–')}
              direction={statistics.getIn(['summary', 'approachesE', 'direction'], 'flat')}
              label="Approaches (E)"
              value={statistics.getIn(['summary', 'approachesE', 'value'], '–')}
            />
            <SummaryTile
              changePercent={statistics.getIn(['summary', 'approachesP', 'changePercent'], '–')}
              direction={statistics.getIn(['summary', 'approachesP', 'direction'], 'flat')}
              label="Approaches (P)"
              value={statistics.getIn(['summary', 'approachesP', 'value'], '–')}
            />
            <SummaryTile
              changePercent={statistics.getIn(['summary', 'approachesCr', 'changePercent'], '–')}
              direction={statistics.getIn(['summary', 'approachesCr', 'direction'], 'flat')}
              label="Approaches (CR)"
              value={statistics.getIn(['summary', 'approachesCr', 'value'], '–')}
            />
            <SummaryTile
              changePercent={statistics.getIn(['summary', 'calls', 'changePercent'], '–')}
              direction={statistics.getIn(['summary', 'calls', 'direction'], 'flat')}
              label="Calls"
              value={statistics.getIn(['summary', 'calls', 'value'], '–')}
            />
            <SummaryTile
              changePercent={statistics.getIn(['summary', 'leads', 'changePercent'], '–')}
              direction={statistics.getIn(['summary', 'leads', 'direction'], 'flat')}
              label="Leads"
              value={statistics.getIn(['summary', 'leads', 'value'], '–')}
            />
            <SummaryTile
              changePercent={statistics.getIn(['summary', 'cc', 'changePercent'], '–')}
              direction={statistics.getIn(['summary', 'cc', 'direction'], 'flat')}
              label="CC"
              value={statistics.getIn(['summary', 'cc', 'value'], '–')}
            />
            <SummaryTile
              changePercent={statistics.getIn(['summary', 'visits', 'changePercent'], '–')}
              direction={statistics.getIn(['summary', 'visits', 'direction'], 'flat')}
              label="Visits"
              value={statistics.getIn(['summary', 'visits', 'value'], '–')}
            />
            <SummaryTile
              changePercent={statistics.getIn(['summary', 'offers', 'changePercent'], '–')}
              direction={statistics.getIn(['summary', 'offers', 'direction'], 'flat')}
              label="Offers"
              value={statistics.getIn(['summary', 'offers', 'value'], '–')}
            />
          </div>
        </div>
      </div>
      <div className="panel panel-default">
        <div className="panel-heading d-flex align-items-center justify-content-space-between">
          <h5 className={style.panelHeading}>
            Firm Rollup — &nbsp;<span className="text-muted">{firmRollupText}</span>
          </h5>
          <div className={style.legend}>
            <span>
              <span className={style.legendG} />
              ≥100%
            </span>
            <span>
              <span className={style.legendA} />
              75–99%
            </span>
            <span>
              <span className={style.legendR} />
              &lt;75%
            </span>
            <span>
              <span className={style.legendX} />
              Exceeds 150%
            </span>
          </div>
        </div>
        <StatisticReportTable
          isLoading={statisticsLoading}
          onShareLink={handleShareLink}
          statistics={statistics}
          user={user}
        />
      </div>
      <div className="row mt5">
        <div className="col-md-12">
          <div className="well">
            <b className="c-primary-dark">Why team-level benchmarks may not equal the sum of titles.</b>
            Team Approach targets are set independently to allow some grace against the title-level totals (e.g., a
            3-person team whose titles sum to 100 might be set at 90). Per-person and team rollups against these
            benchmarks are shown on the <b className="c-primary-dark">Statistics Report</b> tab.
          </div>
        </div>
      </div>
    </>
  );
};

StatisticReportContainer.propTypes = {
  loadStatistics: PropTypes.func.isRequired,
  statisticsLoading: PropTypes.bool.isRequired,
  user: PropTypes.instanceOf(Map).isRequired,
};

function mapStateToProps(state) {
  return {
    statistics: state.directorDashboard.get('statistics'),
    statisticsLoading: state.directorDashboard.get('statisticsLoading'),
    user: state.auth.get('user', null),
  };
}

export { StatisticReportContainer };
export default connect(
  mapStateToProps,
  {
    loadStatistics,
    shareLink,
  },
  mergeProps,
  connectOptions,
)(memo(StatisticReportContainer));
